import { Box, Button as MuiButton, useTheme } from '@mui/material';
import { keyframes } from '@mui/system';
import { type JSX } from 'react';
import { useLocale } from '@repo/i18n';
import { ReactComponent as AccountIcon } from 'src/assets/icons/account.svg';
import Layout from 'src/components/common/timeslots-containers/layouts/layout/Layout';
import Panel from 'src/components/common/timeslots-containers/panel/Panel';
import PanelTitle from 'src/components/common/timeslots-typography/PanelTitle';
import Text from 'src/components/common/timeslots-typography/Text';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { useNativeMemberContext } from './NativeMemberContext';

const reveal = keyframes`
    from {
        opacity: 0;
        transform: translateY(6px);
    }
    to {
        opacity: 1;
        transform: none;
    }
`;

// Native counterpart of the legacy LoggedOutPanel/LoggedInPanel at checkout. Reuses the same
// presentational primitives so it matches the tenant theme, but drives the native login modal and
// reflects native logged-in state. Rendered only for native tenants (ContactView branches on mode).
export default function NativeCheckoutLoginPanel(): JSX.Element | null {
    const { t } = useLocale();
    const theme = useTheme();
    const customizations = useCustomizations();
    const { isNative, isLoggedIn, member, openLoginModal, logout } = useNativeMemberContext();

    if (!isNative) return null;

    const panelSx = {
        width: '100%',
        boxSizing: 'border-box',
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
        border: 'solid',
        [theme.breakpoints.down('md')]: { width: 'auto' },
    };

    if (isLoggedIn && member) {
        return (
            <Panel
                variant="shaded"
                sx={{
                    ...panelSx,
                    animation: `${reveal} 450ms ease both`,
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }}
            >
                <Layout justify="space-between" alignItems="center">
                    <Box aria-live="polite">
                        <Text variant="small">
                            <span>{t.loggedInAs}:</span>
                        </Text>
                        <PanelTitle variant="h3">
                            <span>
                                {member.first_name} {member.last_name}
                            </span>
                        </PanelTitle>
                    </Box>
                    <Box sx={{ flex: '0 0 auto' }}>
                        <MuiButton
                            onClick={() => void logout()}
                            sx={{
                                background: theme.palette.grey['100'],
                                color: theme.typography.body1.color,
                                border: '1px solid',
                                borderColor: theme.palette.grey['200'],
                                '&:hover': { backgroundColor: theme.palette.grey['50'] },
                            }}
                        >
                            {t.logout}
                        </MuiButton>
                    </Box>
                </Layout>
            </Panel>
        );
    }

    return (
        <Panel variant="shaded" sx={panelSx}>
            <Layout justify="space-between" alignItems="center">
                <Box>
                    <PanelTitle>
                        <span>{t.have_account_or_member_card}</span>
                    </PanelTitle>
                    <Text variant="small">
                        <span>{t.pleaseLogInToBook}</span>
                    </Text>
                </Box>
                <Box sx={{ flex: '0 0 auto' }}>
                    <MuiButton
                        startIcon={<AccountIcon style={{ fill: customizations.primaryColor }} />}
                        onClick={openLoginModal}
                        sx={{
                            background: customizations.secondaryColor,
                            color: customizations.secondaryColorContrast,
                            border: '1px solid',
                            borderColor: customizations.primaryColor,
                            '&:hover': {
                                backgroundColor: customizations.lightestGrey,
                                color: customizations.primaryColor,
                            },
                        }}
                    >
                        {t.login}
                    </MuiButton>
                </Box>
            </Layout>
        </Panel>
    );
}
